import { useMemo } from 'react';

import { useHdrProcessingRateMins } from '@/api/devices';
import { HistoricalWaveDataItem, ProcessingSource, WaveDataItemFormattedWithUnits } from '@/api/historicalData';
import { combineProcessingSources } from '@/api/historicalData/helpers';
import { useLiveSplitQuery } from '@/api/useLiveSplitQuery';
import { RequestOptions, axios, requestConfig } from '@/common/axios';
import { convert } from '@/common/convert';
import { CombinedQueryResult, QueryConfig } from '@/common/react-query';
import { APIResponse } from '@/common/types';
import { HISTORICAL_WAVE_DATA_QUERY_KEY } from '@/config';
import { usePreferredUnits } from '@/hooks/usePreferredUnits';
import { DateRange, useGlobalStore } from '@/store';
import { maybeMockNestedHdrResponse } from '@/utils/mockDataHelpers';

export type PostHistoricalWaveDataResponse = APIResponse<{ wavesHistory: HistoricalWaveDataItem[] }>;
export type PostHistoricalWaveDataPayload = {
    spotterId: string;
    startDate: string;
    endDate: string;
    processingSources: ProcessingSource | 'all';
};

export const postHistoricalWaveData = async (
    payload: PostHistoricalWaveDataPayload,
    options?: RequestOptions
): Promise<HistoricalWaveDataItem[]> => {
    const response: PostHistoricalWaveDataResponse = await maybeMockNestedHdrResponse(
        axios.post('/fetch/waves-history', payload, {
            ...requestConfig(options),
        }),
        'wavesHistory'
    );

    return response.data.wavesHistory;
};

type UseHistoricalWaveDataOptions = Omit<PostHistoricalWaveDataPayload, 'spotterId' | 'startDate' | 'endDate'> & {
    spotterId?: string | null;
    dateRange?: DateRange | null;
    config?: Omit<QueryConfig<typeof postHistoricalWaveData>, 'select'>;
};

/**
 * Historical wave data for the range, in the user's preferred units, with embedded samples filling the gaps
 * between the hdr ones.
 */
export const useHistoricalWaveData = ({
    spotterId,
    dateRange,
    processingSources,
    config,
}: UseHistoricalWaveDataOptions): CombinedQueryResult<WaveDataItemFormattedWithUnits[]> => {
    const processingSource = useGlobalStore((state) => state.processingSource);
    const { data: waveDataRaw, ...rest } = useLiveSplitQuery({
        queryKeyPrefix: HISTORICAL_WAVE_DATA_QUERY_KEY,
        spotterId,
        dateRange,
        keyOptions: { processingSources },
        fetchRange: ({ viewToken, signal, ...range }) =>
            postHistoricalWaveData({ ...range, processingSources }, { viewToken, signal }),
        // one of the older Spotter entrypoints, so it excludes the bounds it is given
        apiDateBounds: 'exclusive',
        timestampKey: 'timestamp',
        sortOrder: 'asc',
        /*
         * wavefleet sorts this window oldest first and the route reverses it, so it arrives newest first.
         * Ordered here so the trimming and clipping that reassemble the windows own the order they need.
         */
        responseSortOrder: 'unsorted',
        config,
    });
    const hdrProcessingRateMins = useHdrProcessingRateMins({
        spotterId,
        config: { enabled: config?.enabled !== false },
    });
    const units = usePreferredUnits('distance');
    const formatted = useMemo<WaveDataItemFormattedWithUnits[] | undefined>(
        () =>
            combineProcessingSources(
                waveDataRaw?.map((item) => {
                    return {
                        ...item,
                        significantWaveHeight: convert(item.significantWaveHeight).from('m').to(units),
                        significantWaveHeightUnits: units,
                        significantWaveHeightUnitType: 'length',
                    };
                }),
                hdrProcessingRateMins,
                processingSource
            ),
        [waveDataRaw, hdrProcessingRateMins, units, processingSource]
    );

    return { data: formatted, ...rest };
};
